/**
 * Axios 请求封装
 * @author LWX
 * @date 2026-03-23
 */

import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'
import store from '@/store'

const service = axios.create({
    baseURL: process.env.VUE_APP_BASE_API || '/api',
    timeout: 60000
})

// 是否正在刷新token的标志，防止多个请求同时刷新
let isRefreshing = false
// 等待队列，存放刷新期间的其他请求
let requests = []
// 防止重复退出登录
let isLoggingOut = false

service.interceptors.request.use(
    config => {
        // ===== 优先取客户门户 token =====
        let token = localStorage.getItem('CUSTOMER_PORTAL_TOKEN')

        // 没有客户 token 则取内部员工 token
        if (!token) {
            token = store.getters.token
        }
        if (!token) {
            token = localStorage.getItem('ShareWin-Token')
        }

        if (token) {
            config.headers['Authorization'] = 'Bearer ' + token
        }
        return config
    },
    error => Promise.reject(error)
)

// 响应拦截器 - 统一处理
service.interceptors.response.use(
    response => {
        // 导出文件直接返回，不解析
        if (response.request.responseType === 'blob') {
            return response
        }

        const res = response.data

        // ============================================================
        // 如果是 silent 模式，不弹任何消息
        // ============================================================
        if (response.config.silent) {
            return res
        }

        if (res.code !== 200) {
            // 处理401未授权（token失效）
            if (res.code === 401) {
                // 防止重复跳转
                if (isLoggingOut) {
                    return Promise.reject(new Error('正在退出登录...'))
                }

                isLoggingOut = true

                // 清除所有本地存储
                localStorage.removeItem('ShareWin-Token')
                localStorage.removeItem('ShareWin-Token-Time')
                localStorage.removeItem('ShareWin-User')
                localStorage.removeItem('ShareWin-Menus')
                localStorage.removeItem('ShareWin-Permissions')

                // 清除 store 并跳转登录
                store.dispatch('user/logout').then(() => {
                    setTimeout(() => {
                        isLoggingOut = false
                        router.push('/login')
                        Message({ message: '您的账号已在其他设备登录，请重新登录', type: 'warning', duration: 3000 })
                    }, 100)
                })

                return Promise.reject(new Error('登录已过期，请重新登录'))
            }

            return Promise.reject(new Error(res.msg || '请求失败'))
        }
        return res
    },
    async error => {
        // 文件导出报错不弹通用请求失败
        if (error.config && error.config.responseType === 'blob') {
            Message.error('导出文件失败，请检查数据或稍后重试')
            return Promise.reject(error)
        }

        const originalRequest = error.config

        // 处理401错误并自动刷新token
        if (error.response?.status === 401 && !originalRequest._retry) {
            // 如果是正在退出登录，不刷新token
            if (isLoggingOut) {
                return Promise.reject(error)
            }

            // 如果正在刷新token，将请求加入队列
            if (isRefreshing) {
                return new Promise((resolve) => {
                    requests.push(() => {
                        resolve(service(originalRequest))
                    })
                })
            }

            originalRequest._retry = true
            isRefreshing = true

            try {
                let userId = store.getters.user?.id

                if (!userId) {
                    const userStr = localStorage.getItem('ShareWin-User')
                    if (userStr) {
                        try {
                            const user = JSON.parse(userStr)
                            userId = user.id
                        } catch (e) {
                            console.error('解析用户信息失败', e)
                        }
                    }
                }

                if (!userId) {
                    // 没有用户ID，直接跳转登录
                    if (!isLoggingOut) {
                        isLoggingOut = true
                        localStorage.removeItem('ShareWin-Token')
                        localStorage.removeItem('ShareWin-Token-Time')
                        localStorage.removeItem('ShareWin-User')
                        localStorage.removeItem('ShareWin-Menus')
                        localStorage.removeItem('ShareWin-Permissions')
                        store.dispatch('user/logout')
                        setTimeout(() => {
                            isLoggingOut = false
                            router.push('/login')
                        }, 100)
                    }
                    return Promise.reject(error)
                }

                // 调用后端刷新token接口
                const res = await axios.post('/api/refresh', {
                    userId: userId
                }, {
                    headers: {
                        'Content-Type': 'application/json'
                    }
                })

                if (res.data.code === 200) {
                    const newToken = res.data.data.token

                    localStorage.setItem('ShareWin-Token', newToken)
                    localStorage.setItem('ShareWin-Token-Time', Date.now().toString())
                    store.commit('user/SET_TOKEN', newToken)

                    requests.forEach(cb => cb())
                    requests = []

                    originalRequest.headers['Authorization'] = 'Bearer ' + newToken
                    return service(originalRequest)
                } else {
                    // 刷新失败，清除所有认证信息
                    if (!isLoggingOut) {
                        isLoggingOut = true
                        localStorage.removeItem('ShareWin-Token')
                        localStorage.removeItem('ShareWin-Token-Time')
                        localStorage.removeItem('ShareWin-User')
                        localStorage.removeItem('ShareWin-Menus')
                        localStorage.removeItem('ShareWin-Permissions')
                        store.dispatch('user/logout')
                        setTimeout(() => {
                            isLoggingOut = false
                            router.push('/login')
                        }, 100)
                    }
                    return Promise.reject(error)
                }
            } catch (refreshError) {
                console.error('刷新token失败:', refreshError)
                if (!isLoggingOut) {
                    isLoggingOut = true
                    localStorage.removeItem('ShareWin-Token')
                    localStorage.removeItem('ShareWin-Token-Time')
                    localStorage.removeItem('ShareWin-User')
                    localStorage.removeItem('ShareWin-Menus')
                    localStorage.removeItem('ShareWin-Permissions')
                    store.dispatch('user/logout')
                    setTimeout(() => {
                        isLoggingOut = false
                        router.push('/login')
                    }, 100)
                }
                return Promise.reject(error)
            } finally {
                isRefreshing = false
            }
        }

        // 网络错误等保留弹窗
        Message({ message: error.message || '网络错误', type: 'error', duration: 3000 })
        return Promise.reject(error)
    }
)

export default service